/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, ReactNode, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authClient } from '@services/authClient';
import { SESSION_EXCLUDED_PATHS } from '@utils/publicPaths';
import posthog from '../posthog';

export type AuthUserProps = { userId: string; name: string, email: string, role: string, isBeta?: boolean } | null;
export type SocialProvider = 'google' | 'apple';

// Shape of the session user once enriched by the backend `customSession` plugin.
type SessionUser = {
  id: string;
  userId?: string;
  name?: string;
  email?: string;
  role?: string;
  isBeta?: boolean;
};

interface AuthContextType {
  isAuthenticated: boolean;
  user: AuthUserProps;
  loading: boolean;
  login: (email: string, password: string, rememberMe: boolean) => Promise<void>;
  loginWithProvider: (provider: SocialProvider) => Promise<void>;
  logout: () => Promise<void>;
  signup: (email: string, password: string, first_name: string, gender: string, age: number) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  loading: true,
  login: async () => {},
  loginWithProvider: async () => {},
  logout: async () => {},
  signup: async () => {}
});

const VITE_FLAG_SESSION_STALE_TIME_IN_HOURS = import.meta.env.VITE_FLAG_SESSION_STALE_TIME_IN_HOURS || 4;
const excludedPaths = SESSION_EXCLUDED_PATHS;

const mapUser = (sessionUser: SessionUser | undefined | null): AuthUserProps => {
  if (!sessionUser) return null;
  return {
    userId: sessionUser.userId ?? sessionUser.id,
    name: sessionUser.name ?? '',
    email: sessionUser.email ?? '',
    role: sessionUser.role ?? 'user',
    isBeta: sessionUser.isBeta,
  };
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const isExcluded = excludedPaths.includes(location.pathname);

  const { data: sessionUser, isLoading } = useQuery({
    queryKey: ['auth'],
    queryFn: async () => {
      const { data } = await authClient.getSession();
      return (data?.user ?? null) as SessionUser | null;
    },
    enabled: !isExcluded,
    staleTime: VITE_FLAG_SESSION_STALE_TIME_IN_HOURS * 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const user = mapUser(sessionUser);

  useEffect(() => {
    if (user?.userId) {
      posthog.identify(user.userId, { name: user.name, email: user.email });
    }
  }, [user?.userId, user?.name, user?.email]);

  useEffect(() => {
    const keysToRemove = Object.keys(localStorage).filter(key =>
      key === 'trainingPlanDraft' ||
      key.startsWith('trainingPlanEditDraft-') ||
      key === 'workoutDraft'
    );
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }, []);

  const loginMutation = useMutation({
    mutationFn: async ({ email, password, rememberMe }: { email: string; password: string; rememberMe: boolean }) => {
      const { error } = await authClient.signIn.email({ email, password, rememberMe });
      if (error) throw new Error(error.message || "Échec de la connexion.");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      navigate('/dashboard');
    },
  });

  const signupMutation = useMutation({
    mutationFn: async ({ email, password, first_name, gender, age }: { email: string; password: string; first_name: string; gender: string; age: number }) => {
      const trial_token = sessionStorage.getItem('trial_token') || undefined;
      // gender/age/trial_token are not BetterAuth user fields; they ride along in the
      // request body and are consumed by the backend signup hook.
      const payload = { email, password, name: first_name, gender, age, trial_token };
      const { data, error } = await authClient.signUp.email(
        payload as Parameters<typeof authClient.signUp.email>[0]
      );
      if (error) throw new Error(error.message || "Échec de l'inscription.");
      return data;
    },
    onSuccess: async (data, variables) => {
      const signedUpUser = data?.user as SessionUser | undefined;
      window.Affonso?.signup({
        email: variables.email,
        externalUserId: signedUpUser?.userId ?? signedUpUser?.id ?? '',
      });
      sessionStorage.removeItem('trial_token');

      await queryClient.invalidateQueries({ queryKey: ['auth'] });
      navigate('/onboarding');
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => authClient.signOut(),
    onSuccess: () => {
      posthog.reset();
      queryClient.removeQueries();
      navigate('/');
    },
  });

  const contextValue: AuthContextType = {
    user,
    isAuthenticated: !!user,
    loading: isLoading,
    login: async (email, password, rememberMe) => {
      await loginMutation.mutateAsync({ email, password, rememberMe });
    },
    loginWithProvider: async (provider) => {
      const origin = window.location.origin;
      const params = {
        provider,
        callbackURL: `${origin}/dashboard`,
        newUserCallbackURL: `${origin}/onboarding`,
        errorCallbackURL: `${origin}/login`,
      };
      await authClient.signIn.social(params as Parameters<typeof authClient.signIn.social>[0]);
    },
    logout: async () => {
      await logoutMutation.mutateAsync();
    },
    signup: async (email, password, first_name, gender, age) => {
      await signupMutation.mutateAsync({ email, password, first_name, gender, age });
    },
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// Custom hook for accessing the AuthContext
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
